import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, AlertTriangle, Calendar, MapPin, Phone, FileText, Briefcase, X, CreditCard } from "lucide-react";
import { AuthContext } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRACTICE_TYPES = [
  "EMDR",
  "Somatic Experiencing",
  "Internal Family Systems (IFS)",
  "Brainspotting",
  "Breathwork",
  "Sound Healing",
  "Reiki",
  "Trauma-Informed Yoga",
  "Other",
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

const PractitionerDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Edit form
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/practitioners/me`, { credentials: "include", cache: "no-store" });
        const d = await r.json();
        if (!alive) return;
        if (!d.is_practitioner) {
          setError("This account is not registered as a practitioner. Sign up as a practitioner from the home screen.");
        } else {
          setProfile(d.practitioner);
          setForm({
            name: d.practitioner.name || "",
            practice_type: d.practitioner.practice_type || "EMDR",
            address: d.practitioner.address || "",
            city: d.practitioner.city || "",
            state: d.practitioner.state || "",
            zip: d.practitioner.zip || "",
            phone: d.practitioner.phone || "",
            bio: d.practitioner.bio || "",
          });
        }
      } catch {
        if (alive) setError("Could not load your practitioner profile. Please try again.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authLoading, user, navigate]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!form) return;
    setSaving(true); setError(""); setSuccess("");
    try {
      const r = await fetch(`${API}/practitioners/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not save");
      setProfile(d.practitioner);
      setSuccess("Saved.");
      setTimeout(() => setSuccess(""), 2400);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true); setError(""); setSuccess("");
    try {
      const r = await fetch(`${API}/practitioners/cancel`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not cancel");
      setProfile(d.practitioner);
      setConfirmCancel(false);
      setSuccess("Your listing has been cancelled. Active until your current premium period ends.");
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true); setError("");
    try {
      const r = await fetch(`${API}/practitioners/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(d.detail || "Could not start checkout");
      window.location.href = d.url;
    } catch (err) {
      setError(err.message);
      setSubscribing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-sm" style={{ color: "#5C5470" }}>
        Loading your practitioner dashboard…
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <AlertTriangle size={32} style={{ color: "#D4A574" }} />
        <p className="mt-3 max-w-sm" style={{ color: "#5C5470" }}>{error}</p>
        <button onClick={() => navigate("/")} className="mt-5 px-5 py-2 rounded-full text-xs font-bold"
          style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)", color: "#FFF" }}
          data-testid="practitioner-back-home">
          Back to Primordeia
        </button>
      </div>
    );
  }

  const dLeft = daysUntil(profile?.premium_until);
  const active = !!profile?.active;
  const inGrace = dLeft !== null && dLeft >= 0;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="min-h-screen px-4 py-8 max-w-2xl mx-auto" data-testid="practitioner-dashboard">
      <button onClick={() => navigate("/")} className="flex items-center gap-1 text-xs font-semibold mb-5 hover:opacity-80"
        style={{ color: "#4F378B" }} data-testid="practitioner-back">
        <ArrowLeft size={14} /> Back to Primordeia
      </button>

      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: "#3D2A0E" }}
        data-testid="practitioner-title">
        Practitioner Dashboard
      </h1>
      <p className="text-xs italic mb-6" style={{ color: "#7B5EA7" }}>
        {profile?.email} &middot; {profile?.practice_type}
      </p>

      {/* Status card */}
      <div className="rounded-2xl p-5 mb-6"
        style={{
          background: active && inGrace
            ? "linear-gradient(135deg, rgba(212,165,116,0.15), rgba(123,94,167,0.12))"
            : "linear-gradient(135deg, rgba(192,57,43,0.08), rgba(192,57,43,0.04))",
          border: "1px solid rgba(212,165,116,0.4)"
        }}
        data-testid="practitioner-status">
        <div className="flex items-center gap-2 mb-2">
          {active && inGrace ? <CheckCircle size={16} style={{ color: "#4CAF50" }} />
                              : <AlertTriangle size={16} style={{ color: "#C0392B" }} />}
          <span className="text-sm font-bold" style={{ color: "#3D2A0E", fontFamily: "'Fredoka', sans-serif" }}>
            {!active ? "Listing cancelled" : (inGrace ? "Listing active" : "Listing inactive — premium expired")}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs" style={{ color: "#5C5470" }}>
          <Calendar size={12} />
          <span data-testid="practitioner-premium-until">
            {profile?.card_on_file ? "Renews" : "Free trial ends"}: <strong>{formatDate(profile?.premium_until)}</strong>
            {dLeft !== null && dLeft >= 0 && <> &middot; {dLeft} day{dLeft === 1 ? "" : "s"} left</>}
          </span>
        </div>
        {!profile?.card_on_file && active && inGrace && (
          <button onClick={handleSubscribe} disabled={subscribing}
            className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold"
            style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)", color: "#FFF", opacity: subscribing ? 0.7 : 1 }}
            data-testid="practitioner-add-card">
            <CreditCard size={14} className="inline-block mr-2" />
            {subscribing ? "Opening Stripe…" : "Add card — $7.59/mo after free trial"}
          </button>
        )}
        {!active && (
          <p className="text-[11px] mt-3 italic" style={{ color: "#5C5470" }}>
            Cancelled. Your listing will remain visible until {formatDate(profile?.premium_until)}; no future charges.
          </p>
        )}
      </div>

      {/* Edit form */}
      <form onSubmit={handleSave} className="rounded-2xl p-5 space-y-3"
        style={{ background: "#FFF", border: "1px solid #E8E0F0" }}>
        <h2 className="text-sm font-bold mb-1" style={{ color: "#3D2A0E", fontFamily: "'Fredoka', sans-serif" }}>
          Your listing
        </h2>

        <label className="block">
          <span className="text-[11px] font-semibold flex items-center gap-1 mb-1" style={{ color: "#5C5470" }}>
            Display name
          </span>
          <input type="text" value={form?.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-name" />
        </label>

        <label className="block">
          <span className="text-[11px] font-semibold flex items-center gap-1 mb-1" style={{ color: "#5C5470" }}>
            <Briefcase size={11} /> Practice type
          </span>
          <select value={form?.practice_type || ""} onChange={e => setForm(f => ({ ...f, practice_type: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-practice-type">
            {PRACTICE_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-[11px] font-semibold flex items-center gap-1 mb-1" style={{ color: "#5C5470" }}>
            <MapPin size={11} /> Street address
          </span>
          <input type="text" value={form?.address || ""} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-address" />
        </label>

        <div className="flex gap-2">
          <input type="text" placeholder="City" value={form?.city || ""}
            onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
            className="flex-[2] px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-city" />
          <input type="text" placeholder="State" maxLength={2} value={form?.state || ""}
            onChange={e => setForm(f => ({ ...f, state: e.target.value.toUpperCase() }))}
            className="flex-[1] px-3 py-2 rounded-lg border text-xs uppercase" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-state" />
          <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="ZIP" maxLength={5}
            value={form?.zip || ""} onChange={e => setForm(f => ({ ...f, zip: e.target.value.replace(/\D/g, "") }))}
            className="flex-[1.2] px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-zip" />
        </div>

        <label className="block">
          <span className="text-[11px] font-semibold flex items-center gap-1 mb-1" style={{ color: "#5C5470" }}>
            <Phone size={11} /> Phone
          </span>
          <input type="tel" value={form?.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-phone" />
        </label>

        <label className="block">
          <span className="text-[11px] font-semibold flex items-center gap-1 mb-1" style={{ color: "#5C5470" }}>
            <FileText size={11} /> Short bio
          </span>
          <textarea value={form?.bio || ""} rows={3} maxLength={500}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg border text-xs resize-none" style={{ borderColor: "#D4C4E8" }}
            data-testid="practitioner-edit-bio" />
          <span className="text-[10px] italic" style={{ color: "#7B5EA7" }}>{(form?.bio || "").length} / 500</span>
        </label>

        {error && <p className="text-xs" style={{ color: "#C0392B" }} data-testid="practitioner-error">{error}</p>}
        {success && <p className="text-xs" style={{ color: "#4CAF50" }} data-testid="practitioner-success">{success}</p>}

        <button type="submit" disabled={saving}
          className="w-full py-2.5 rounded-xl text-sm font-bold"
          style={{ background: "linear-gradient(135deg, #4F378B, #7B5EA7)", color: "#FFF", opacity: saving ? 0.7 : 1 }}
          data-testid="practitioner-save-button">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      {/* Cancel section */}
      {active && (
        <div className="mt-6 rounded-2xl p-5"
          style={{ background: "#FBF6F4", border: "1px solid #F0D4CC" }}
          data-testid="practitioner-cancel-section">
          {!confirmCancel ? (
            <button onClick={() => setConfirmCancel(true)}
              className="text-xs font-semibold underline" style={{ color: "#C0392B" }}
              data-testid="practitioner-cancel-button">
              Cancel my practitioner listing
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-xs" style={{ color: "#3D2A0E" }}>
                <strong>Are you sure?</strong> Your listing will be removed from the EMDR Trauma Chill Zone after {formatDate(profile?.premium_until)}. No future charges. You can re-activate any time.
              </p>
              <div className="flex gap-2">
                <button onClick={handleCancel} disabled={cancelling}
                  className="flex-1 py-2 rounded-xl text-xs font-bold"
                  style={{ background: "#C0392B", color: "#FFF", opacity: cancelling ? 0.7 : 1 }}
                  data-testid="practitioner-confirm-cancel">
                  {cancelling ? "Cancelling…" : "Yes, cancel"}
                </button>
                <button onClick={() => setConfirmCancel(false)}
                  className="flex-1 py-2 rounded-xl text-xs font-bold"
                  style={{ background: "rgba(192,57,43,0.08)", color: "#C0392B" }}
                  data-testid="practitioner-keep-listing">
                  <X size={11} className="inline mr-1" /> Keep my listing
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default PractitionerDashboard;
